import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useSearchParams, useLocation } from "react-router-dom";
import ShopProductGrid from "./ShopProductGrid";
import CategoryFilter from "./CategoryFilter";
import { productService } from "../../api/productService";

const Wrapper = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;

  @media (max-width: 768px) {
    padding: 0 0.5rem;
  }
`;

const Main = styled.main`
  width: 100%;
`;

const ShopPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const [categories, setCategories] = useState<string[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedProductId, setSelectedProductId] = useState<string | null>(
    null
  );

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const products = await productService.getAllProducts();
        const uniqueCategories = Array.from(
          new Set(products.map((product) => product.category))
        ).filter(Boolean);
        setCategories(uniqueCategories);
      } catch (error) {
        console.error("Failed to fetch categories:", error);
      }
    };

    fetchCategories();
  }, []);

  // Handle category from URL query parameter
  useEffect(() => {
    const categoryFromUrl = searchParams.get("category");
    if (categoryFromUrl) {
      // Decode the category from URL (handles spaces and special characters)
      const decodedCategory = decodeURIComponent(categoryFromUrl);
      setSelectedCategory(decodedCategory);
    } else {
      // Clear selected category if no category parameter in URL
      setSelectedCategory(null);
    }
  }, [searchParams]);

  // Handle navigation from featured products
  useEffect(() => {
    if (location.state && (location.state as any).selectedProductId) {
      setSelectedProductId((location.state as any).selectedProductId);
    }
  }, [location.state]);

  const handleCategorySelect = (category: string | null) => {
    setSelectedCategory(category);
    // Update URL with selected category
    const newSearchParams = new URLSearchParams(searchParams);
    if (category) {
      // Encode category to handle spaces and special characters
      newSearchParams.set("category", encodeURIComponent(category));
    } else {
      newSearchParams.delete("category");
    }
    setSearchParams(newSearchParams, { replace: true });
    // Reset selected product when category changes
    setSelectedProductId(null);
  };

  return (
    <Wrapper>
      <Main>
        <CategoryFilter
          categories={categories}
          selectedCategory={selectedCategory}
          onCategorySelect={handleCategorySelect}
        />
        <ShopProductGrid
          selectedCategory={selectedCategory}
          selectedProductId={selectedProductId}
        />
      </Main>
    </Wrapper>
  );
};

export default ShopPage;
